// @ts-strict-ignore
import React from 'react';
import type { ColumnDef } from '@usealloy/component-library';
import {
  Icon,
  IconName,
} from '@usealloy/component-library';
import moment from 'moment';
import type { MatchData, WatchlistResponse } from './types';
import { MatchStrengthBadge, StatusBadge } from './components';

const datumOrDashes = (getValue, callback) => {
  const value = getValue();
  if (!value) return '-';
  return callback(value);
};

export const resolveModalTableColumns: ColumnDef<MatchData>[] = [
  {
    accessorFn: ({ name }) => name,
    enableSorting: false,
    header: 'Name',
    id: 'name',
    maxSize: 160,
  },
  {
    accessorFn: ({ screening_type }) => screening_type,
    enableSorting: false,
    header: 'Screening Type',
    id: 'screeningType',
    maxSize: 160,
  },
  {
    accessorFn: ({ service }) => service,
    enableSorting: false,
    header: 'Service',
    id: 'service',
    maxSize: 160,
  },
  {
    accessorFn: ({ match_types }) => match_types.map(({ label }) => label).join(', '),
    cell: ({ getValue }) => datumOrDashes(
      getValue,
      value => value,
    ),
    enableSorting: false,
    header: 'Type',
    id: 'matchTypes',
    maxSize: 160,
  },
  {
    accessorFn: ({ match_fields }) => match_fields.join(', '),
    cell: ({ getValue }) => datumOrDashes(
      getValue,
      value => value,
    ),
    enableSorting: false,
    header: 'Match Fields',
    id: 'matchFields',
    maxSize: 160,
  },
  {
    accessorFn: ({ match_strength }) => match_strength,
    header: 'Strength',
    cell: ({ getValue, row: { original: { service } } }) => datumOrDashes(
      getValue,
      value => <MatchStrengthBadge service={service} strength={value as number} />,
    ),
    id: 'matchStrength',
    size: 120,
  },
  {
    accessorFn: ({ last_updated }) => last_updated,
    cell: ({ getValue }) => datumOrDashes(
      getValue,
      value => moment(value).format('MM-DD-yyyy'),
    ),
    header: 'Last Updated',
    id: 'lastUpdated',
    size: 140,
  },
];

export const reviewTableColumns: ColumnDef<MatchData>[] = [
  {
    accessorFn: ({ name }) => name,
    header: 'Name',
    id: 'name',
    size: 140,
  },
  {
    accessorFn: ({ screening_type }) => screening_type,
    header: 'Screening Type',
    id: 'screeningType',
    size: 160,
  },
  {
    accessorFn: ({ service }) => service,
    header: 'Service',
    id: 'service',
    size: 140,
  },
  {
    accessorFn: ({ match_types }) => match_types.map(({ label }) => label).join(', '),
    enableSorting: false,
    id: 'matchTypes',
    header: 'Types',
    size: 140,
  },
  {
    accessorFn: ({ match_fields }) => match_fields.join(', '),
    cell: ({ getValue }) => datumOrDashes(
      getValue,
      value => value,
    ),
    enableSorting: false,
    header: 'Match Fields',
    id: 'matchFields',
    size: 140,
  },
  {
    accessorFn: ({ match_strength }) => match_strength,
    header: 'Strength',
    cell: ({ getValue, row: { original: { service } } }) => datumOrDashes(
      getValue,
      value => <MatchStrengthBadge service={service} strength={value as number} />,
    ),
    id: 'matchStrength',
    size: 120,
  },
  {
    accessorFn: ({ last_updated }) => last_updated,
    cell: ({ getValue }) => datumOrDashes(
      getValue,
      value => moment(value).format('MM-DD-yyyy'),
    ),
    header: 'Last Updated',
    id: 'lastUpdated',
    size: 140,
  },
];

export const allTableColumns: ColumnDef<MatchData>[] = [
  ...reviewTableColumns,
  {
    accessorFn: ({ status }) => status,
    cell: ({ getValue }) => (<StatusBadge status={getValue() as string} />),
    enableSorting: false,
    header: 'Status',
    id: 'status',
    maxSize: 140,
    minSize: 85,
    size: 85,
  },
  {
    accessorFn: ({ resolution }) => resolution.note,
    enableSorting: false,
    header: 'Note',
    id: 'note',
    size: 140,
  },
  {
    accessorFn: ({ is_suppressed }) => is_suppressed,
    cell: ({ getValue }) => (
      getValue()
        ? (
          <Icon icon={IconName.Approved} size="large" />
        ) : (
          <div />
        )
    ),
    enableSorting: false,
    header: 'Suppressed',
    id: 'isSuppressed',
    maxSize: 140,
    minSize: 85,
    size: 85,
  },
];

export const aiAssistedColumn: ColumnDef<MatchData> = {
  accessorFn: ({ is_ai_assisted }) => is_ai_assisted,
  cell: ({ getValue, row }) => {
    const value = getValue();
    const status = row?.original?.status;
    const normalizedStatus = typeof status === 'string' ? status.trim().toLowerCase() : null;

    if (value === null || value === undefined) return '-';
    if (normalizedStatus === 'needs review' || normalizedStatus === 'needs_review') return '-';
    return value ? 'Yes' : 'No';
  },
  enableSorting: false,
  header: 'AI Assisted',
  id: 'isAiAssisted',
  size: 110,
};

export const getAllTableColumns = (
  showAiAssisted: boolean,
): ColumnDef<MatchData>[] => (
  showAiAssisted
    ? [...allTableColumns, aiAssistedColumn]
    : allTableColumns
);

export const initialWatchlistData = { matches: [], service: '' } as WatchlistResponse;

export const resolutionDecisionReasonOptions = [
  { label: 'No Match', value: 'No Match' },
  { label: 'False Positive', value: 'False Positive' },
  { label: 'Potential Match', value: 'Potential Match' },
  { label: 'True Match', value: 'True Match' },
  { label: 'Unknown', value: 'Unknown' },
];
